import React from "react";
import { Link } from "react-router-dom";
import { DoorOpen, Heart, Shield, Users, ArrowRight, AlertTriangle } from "lucide-react";

/**
 * MAVULA Investment Ltd — About Us
 * Positioning: structured purchase pathway — NOT an investment product.
 * This page and the accompanying /legal-status page keep MAVULA legally
 * defensible under UK FSMA / FCA rules while we work toward authorisation.
 */
export default function AboutPage() {
  return (
    <main data-testid="about-page" className="bg-white">
      {/* Hero */}
      <section className="mv-green-bg text-white py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 opacity-15" style={{ background: "radial-gradient(600px 300px at 20% 0%, var(--mv-gold), transparent 60%)" }}/>
        <div className="max-w-5xl mx-auto px-4 md:px-8 relative">
          <div className="text-[11px] uppercase tracking-[0.28em]" style={{ color: "var(--mv-gold)" }}>§ About Us</div>
          <h1 className="font-display text-4xl md:text-6xl mt-4 leading-[1.02]">
            Mavula <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>Investment Ltd</span>
          </h1>
          <p className="mt-6 font-accent italic text-xl md:text-2xl text-white/85 max-w-3xl">
            "Mavula" means Open Doors — opening doors to home ownership and fair opportunity for Southern Africans at home and abroad.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 md:py-24">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>§ Our Story</span></div>
          <h2 className="font-display text-3xl md:text-4xl mv-green-text mt-3 leading-[1.05]">
            Why we exist.
          </h2>
          <div className="mt-6 space-y-5 text-[15.5px] leading-relaxed" style={{ color: "var(--mv-ink)" }}>
            <p>
              We were founded because we saw too many hard-working people unable to build a future through no fault of their own. Banks set impossible requirements, fees are unfair, and many people living or working overseas struggle to invest back home where their hearts are.
            </p>
            <p>
              We are here to change that. Not with tricks or shortcuts, but with honesty, transparency, and respect. We provide a clear, structured pathway toward owning land or a home, fairer terms than you will find elsewhere, and full support every step of the way. We are proudly registered in the United Kingdom and operate with deep roots across Southern Africa.
            </p>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-20 md:py-24 border-t mv-hairline" style={{ background: "var(--mv-bg)" }}>
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>§ What We Do</span></div>
          <h2 className="font-display text-3xl md:text-4xl mv-green-text mt-3 leading-[1.05]">
            A structured purchase pathway — <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>step by step.</span>
          </h2>
          <p className="mt-4 max-w-2xl text-[15px]" style={{ color: "var(--mv-muted)" }}>
            We connect people looking to buy property with verified opportunities, and help you pay toward ownership in manageable steps:
          </p>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              "Clear deposit agreed upfront",
              "Structured affordable payments until fully settled",
              "Title deed registered fully in your name — no hidden ownership structures",
              "Secure processing through licensed payment partners",
              "Guidance through every legal and administrative step",
            ].map((t) => (
              <div key={t} className="mv-card p-5 flex items-start gap-3" data-testid="about-what-we-do-item">
                <div className="w-8 h-8 rounded-2xl flex items-center justify-center shrink-0 mv-green-text" style={{ background: "rgba(212,175,55,0.16)" }}>
                  <Shield size={16}/>
                </div>
                <div className="text-[14.5px] mv-green-text font-medium">{t}</div>
              </div>
            ))}
          </div>

          {/* Important Clarification */}
          <div className="mt-8 mv-card p-5 md:p-6 flex items-start gap-3"
               style={{ background: "rgba(212,60,60,0.05)", borderColor: "rgba(212,60,60,0.20)" }}
               data-testid="about-clarification">
            <AlertTriangle size={20} className="mt-0.5 shrink-0" style={{ color: "#8a2020" }}/>
            <div>
              <div className="font-display text-[15.5px]" style={{ color: "#8a2020" }}>Important Clarification</div>
              <p className="mt-1 text-[13.5px]" style={{ color: "var(--mv-ink)" }}>
                This is a structured purchase pathway — <strong>not an investment scheme, savings account, or financial product that earns interest or guaranteed returns</strong>. Property values may rise or fall according to market conditions. We do not promise fixed yields, profits, or dividends.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-24">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>§ Our Values</span></div>
          <h2 className="font-display text-3xl md:text-4xl mv-green-text mt-3 leading-[1.05]">
            Four rules we live by.
          </h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { i: <Shield size={22}/>, t: "Honesty first", d: "We tell you exactly what is possible, and what is not." },
              { i: <Users size={22}/>,  t: "Fairness for all", d: "We do not favour those with more money over those with strong commitment." },
              { i: <DoorOpen size={22}/>, t: "Respect for rules", d: "We follow every law, and work openly with regulators." },
              { i: <Heart size={22}/>,  t: "Community at heart", d: "We exist to lift people up, not profit from hardship." },
            ].map((v) => (
              <div key={v.t} className="mv-card p-6" data-testid="about-value">
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-3 mv-green-text" style={{ background: "rgba(212,175,55,0.16)" }}>{v.i}</div>
                <div className="font-display text-xl mv-green-text">{v.t}</div>
                <p className="mt-1 text-[14px]" style={{ color: "var(--mv-muted)" }}>{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 md:py-24 mv-green-bg text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-15" style={{ background: "radial-gradient(500px 250px at 80% 100%, var(--mv-gold), transparent 60%)" }}/>
        <div className="max-w-4xl mx-auto px-4 md:px-8 relative">
          <div className="text-[11px] uppercase tracking-[0.28em]" style={{ color: "var(--mv-gold)" }}>§ Our Mission</div>
          <h2 className="font-display text-3xl md:text-5xl mt-4 leading-[1.02]">
            To make dignified home ownership a reality for every person who works for it — <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>whether you are in the UK, Southern Africa, or anywhere in the world.</span>
          </h2>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/projects" className="btn-gold" data-testid="about-projects-cta">
              See Projects <ArrowRight size={16}/>
            </Link>
            <Link to="/legal-status" className="btn-ghost !bg-white/10 !text-white !border-white/30 hover:!bg-white/20" data-testid="about-legal-cta">
              Read Legal Status
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
